import React, { useEffect, useState, useMemo } from 'react';
import { faqApi } from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import {
    Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../ui/accordion';
import { HelpCircle, Search, Loader2 } from 'lucide-react';
import { MemberHeader } from '../common/MemberHeader';
import { useAuth } from '../../contexts/AuthContext';

export default function PortalHelpPage() {
    const [data, setData] = useState(null);
    const [search, setSearch] = useState('');
    const { user: _u } = useAuth();

    useEffect(() => {
        faqApi.portalList()
            .then(r => setData(r.data))
            .catch(() => setData({ categories: [] }));
    }, []);

    const filtered = useMemo(() => {
        if (!data) return [];
        const q = search.trim().toLowerCase();
        if (!q) return data.categories;
        return data.categories
            .map(c => ({
                ...c,
                faqs: c.faqs.filter(f =>
                    f.question.toLowerCase().includes(q) ||
                    (f.answer_html || '').toLowerCase().includes(q)
                ),
            }))
            .filter(c => c.faqs.length > 0);
    }, [data, search]);

    return (
        <div className="min-h-screen bg-muted">
            <MemberHeader />
            <main className="max-w-5xl mx-auto px-4 py-8">
                <div className="flex items-center gap-2 mb-2">
                    <HelpCircle className="h-7 w-7 text-primary" />
                    <h1 className="text-2xl font-heading font-bold">Hilfe & Häufige Fragen</h1>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                    Hier finden Sie Antworten auf die wichtigsten Fragen rund um Ihre Buchungen, Lizenzen und Ihr Konto.
                    Wenn Sie Ihre Frage hier nicht finden, wenden Sie sich gerne direkt per E-Mail an uns.
                </p>

                <div className="relative mb-6 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Frage durchsuchen…"
                        className="pl-9"
                        data-testid="portal-help-search"
                    />
                </div>

                {!data ? (
                    <div className="text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Lade…</div>
                ) : filtered.length === 0 ? (
                    <Card><CardContent className="p-8 text-center text-sm text-muted-foreground italic">
                        {search ? `Keine Treffer für „${search}".` : 'Aktuell sind keine FAQs veröffentlicht.'}
                    </CardContent></Card>
                ) : (
                    <div className="flex flex-col gap-6">
                        {filtered.map(cat => (
                            <Card key={cat.id} data-testid={`help-cat-${cat.id}`}>
                                <CardContent className="p-0">
                                    <h2 className="px-5 py-3 border-b font-heading font-semibold text-base bg-gray-50">{cat.label}</h2>
                                    <Accordion type="multiple">
                                        {cat.faqs.map(f => (
                                            <AccordionItem key={f.id} value={f.id} className="border-b last:border-0">
                                                <AccordionTrigger className="px-5 text-left text-sm font-medium hover:no-underline" data-testid={`help-q-${f.id}`}>
                                                    {f.question}
                                                </AccordionTrigger>
                                                <AccordionContent className="px-5 pb-4">
                                                    <div className="prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1" dangerouslySetInnerHTML={{ __html: f.answer_html || '' }} />
                                                </AccordionContent>
                                            </AccordionItem>
                                        ))}
                                    </Accordion>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
